import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { DataTablePageSkeleton } from '../components/common/Skeleton';
import { PageFadeSection } from '../components/common/PageFadeSection';
import { 
  getTeams, 
  createTeam, 
  updateTeam, 
  deleteTeam,
  addTeamMember,
  removeTeamMember 
} from '../api/teamApi';
import { getLeads, getDepartments, getAllUsers, getUsersByRole } from '../api/adminApi';
import { parseUTC } from '../utils/dateUtils';

const TeamManagementPage = () => {
  const [teams, setTeams] = useState([]);
  const [leads, setLeads] = useState([]);
  const [pms, setPms] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', 
    description: '', 
    department: '', 
    leadUserId: '',
    pmUserId: ''
  });
  const [processing, setProcessing] = useState(false);
  const [filterDepartment, setFilterDepartment] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [showLeadWarning, setShowLeadWarning] = useState(false);
  const [leadWarningData, setLeadWarningData] = useState(null);
  const [showOnlyMyTeams, setShowOnlyMyTeams] = useState(false);
  
  // Get current user info from localStorage/auth context
  const [currentUserRole, setCurrentUserRole] = useState(0);
  const [currentUserId, setCurrentUserId] = useState('');

  useEffect(() => {
    // Get current user info from your authentication system
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    setCurrentUserRole(userData.role || 0);
    setCurrentUserId(userData.id || '');
    
    loadData();
  }, [filterDepartment]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [teamsResult, leadsResult, usersResult, deptsResult, pmsResult] = await Promise.all([
        getTeams(filterDepartment || null),
        getLeads(),
        getAllUsers(),
        getDepartments(),
        getUsersByRole(3) // PM role = 3
      ]);

      if (teamsResult.isSuccess) {
        setTeams(teamsResult.data || []);
      } else {
        toast.error(teamsResult.message || 'Failed to load teams');
      }
      
      if (leadsResult.isSuccess) setLeads(leadsResult.data || []);
      if (usersResult.isSuccess) setAllUsers(usersResult.data || []);
      if (pmsResult.isSuccess) setPms(pmsResult.data || []);
      setDepartments((deptsResult || []).sort((a, b) => a.name.localeCompare(b.name)));
      
      setError(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.errors?.join(', ') ||
        err.message ||
        'Failed to load data';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Permission checks based on role hierarchy
  const canCreateTeam = () => {
    // Only Admin (4) and PM (3) can create teams
    return currentUserRole === 4 || currentUserRole === 3;
  };

  const isPmOfTeam = (team) => team.pmUserId === currentUserId;

  const canEditTeam = (team) => {
    // Admin can edit any team
    if (currentUserRole === 4) return true;
    
    // PM can only edit teams where they are the PM
    if (currentUserRole === 3 && isPmOfTeam(team)) return true;
    
    // Lead can only edit their own team
    if (currentUserRole === 2 && team.leadUserId === currentUserId) return true;
    
    return false;
  };

  const canDeleteTeam = (team) => {
    // Admin can delete any team
    if (currentUserRole === 4) return true;
    // PM can only delete teams where they are the PM
    if (currentUserRole === 3 && team && isPmOfTeam(team)) return true;
    return false;
  };

  const canManageMembers = (team) => {
    // Admin can manage any team members
    if (currentUserRole === 4) return true;

    // PM can only manage members in teams where they are the PM
    if (currentUserRole === 3 && isPmOfTeam(team)) return true;

    // Lead can only manage members in their own team
    if (currentUserRole === 2 && team.leadUserId === currentUserId) return true;
    
    return false;
  };

  const canRemoveMember = (team, member) => {
    // Can't remove the team lead
    if (member.userId === team.leadUserId) return false;
    
    return canManageMembers(team);
  };

  const handleCreateTeam = async () => {
    if (!formData.name || !formData.leadUserId) {
      toast.error('Team name and lead are required');
      return;
    }

    // Check permission
    if (!canCreateTeam()) {
      toast.error('You do not have permission to create teams');
      return;
    }

    // Check if lead already has teams (multi-team lead warning)
    const existingLeadTeams = teams.filter(t => t.leadUserId === formData.leadUserId);
    if (existingLeadTeams.length > 0 && !leadWarningData?.confirmed) {
      const leadName = leads.find(l => l.id === formData.leadUserId)?.fullName || 'This team lead';
      setLeadWarningData({ 
        leadName, 
        teamCount: existingLeadTeams.length,
        action: 'create'
      });
      setShowLeadWarning(true);
      return;
    }

    try {
      setProcessing(true);
      const submitData = { ...formData };
      // If current user is PM, auto-assign their userId as PM
      if (currentUserRole === 3) {
        submitData.pmUserId = currentUserId;
      }
      const result = await createTeam(submitData);
      if (result.isSuccess) {
        setShowCreateModal(false);
        setFormData({ name: '', description: '', department: '', leadUserId: '', pmUserId: '' });
        setLeadWarningData(null);
        loadData();
        toast.success('Team created successfully');
      } else {
        toast.error(result.errorMessage || 'Failed to create team');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.errors?.join(', ') ||
        err.message ||
        'Failed to create team';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleUpdateTeam = async () => {
    if (!selectedTeam) return;

    // Check permission
    if (!canEditTeam(selectedTeam)) {
      toast.error('You do not have permission to edit this team');
      return;
    }

    // Check if lead changed and new lead already has teams
    if (formData.leadUserId && formData.leadUserId !== selectedTeam.leadUserId) {
      const existingLeadTeams = teams.filter(t => t.leadUserId === formData.leadUserId && t.id !== selectedTeam.id);
      if (existingLeadTeams.length > 0 && !leadWarningData?.confirmed) {
        const leadName = leads.find(l => l.id === formData.leadUserId)?.fullName || 'This team lead';
        setLeadWarningData({ 
          leadName, 
          teamCount: existingLeadTeams.length,
          action: 'update'
        });
        setShowLeadWarning(true);
        return;
      }
    }

    try {
      setProcessing(true);
      const result = await updateTeam(selectedTeam.id, formData);
      if (result.isSuccess) {
        setShowEditModal(false);
        setSelectedTeam(null);
        setFormData({ name: '', description: '', department: '', leadUserId: '', pmUserId: '' });
        setLeadWarningData(null);
        loadData();
        toast.success('Team updated successfully');
      } else {
        toast.error(result.errorMessage || 'Failed to update team');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.errors?.join(', ') ||
        err.message ||
        'Failed to update team';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!teamToDelete) return;

    // Check permission — find the team object for role check
    const teamObj = teams.find(t => t.id === teamToDelete);
    if (!canDeleteTeam(teamObj)) {
      toast.error('You do not have permission to delete this team');
      return;
    }

    try {
      const result = await deleteTeam(teamToDelete);
      if (result.isSuccess) {
        loadData();
        setShowDeleteConfirmation(false);
        setTeamToDelete(null);
        toast.success('Team deleted successfully');
      } else {
        toast.error(result.errorMessage || 'Failed to delete team');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message ||
        err.response?.data?.errors?.join(', ') ||
        err.message ||
        'Failed to delete team';
      toast.error(errorMessage);
    }
  };

  const confirmDeleteTeam = (teamId) => {
    // Check permission before showing confirmation
    const teamObj = teams.find(t => t.id === teamId);
    if (!canDeleteTeam(teamObj)) {
      toast.error('You do not have permission to delete this team');
      return;
    }
    setTeamToDelete(teamId);
    setShowDeleteConfirmation(true);
  };

  const handleAddMember = async (userId) => {
    if (!selectedTeam) return;

    // Check permission
    if (!canManageMembers(selectedTeam)) {
      toast.error('You do not have permission to add members to this team');
      return;
    }

    const addPromise = new Promise(async (resolve, reject) => {
      try {
        setProcessing(true);
        const result = await addTeamMember(selectedTeam.id, userId);
        if (result.isSuccess) {
          loadData();
          // Refresh selected team
          const updatedTeams = await getTeams(filterDepartment || null);
          if (updatedTeams.isSuccess) {
            const updated = updatedTeams.data.find(t => t.id === selectedTeam.id);
            if (updated) setSelectedTeam(updated);
          }
          resolve('Member added successfully');
        } else {
          reject(new Error(result.errorMessage || 'Failed to add member'));
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message ||
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to add member';
        reject(new Error(errorMessage));
      } finally {
        setProcessing(false);
      }
    });

    toast.promise(addPromise, {
      loading: 'Adding member...',
      success: (message) => message,
      error: (err) => err.message || 'Failed to add member',
    });
  };

  const handleAddSelectedMembers = async () => {
    if (!selectedTeam || selectedUsers.length === 0) {
      toast.error('Please select at least one member to add');
      return;
    }

    // Check permission
    if (!canManageMembers(selectedTeam)) {
      toast.error('You do not have permission to add members to this team');
      return;
    }

    const addPromise = new Promise(async (resolve, reject) => {
      try {
        setProcessing(true);
        
        // Add all selected users
        const promises = selectedUsers.map(userId => 
          addTeamMember(selectedTeam.id, userId)
        );
        
        const results = await Promise.all(promises);
        const allSuccess = results.every(result => result.isSuccess);
        
        if (allSuccess) {
          loadData();
          // Refresh selected team
          const updatedTeams = await getTeams(filterDepartment || null);
          if (updatedTeams.isSuccess) {
            const updated = updatedTeams.data.find(t => t.id === selectedTeam.id);
            if (updated) setSelectedTeam(updated);
          }
          
          setSelectedUsers([]);
          setSearchTerm('');
          setIsDropdownOpen(false);
          
          resolve(`Successfully added ${selectedUsers.length} member(s)`);
        } else {
          reject(new Error('Failed to add some members'));
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message ||
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to add members';
        reject(new Error(errorMessage));
      } finally {
        setProcessing(false);
      }
    });

    toast.promise(addPromise, {
      loading: `Adding ${selectedUsers.length} member(s)...`,
      success: (message) => message,
      error: (err) => err.message || 'Failed to add members',
    });
  };

  const handleRemoveMember = async () => {
    if (!selectedTeam || !userToRemove) return;

    // Check permission
    const member = selectedTeam.members?.find(m => m.userId === userToRemove);
    if (!member || !canRemoveMember(selectedTeam, member)) {
      toast.error('You do not have permission to remove this member');
      return;
    }

    const removePromise = new Promise(async (resolve, reject) => {
      try {
        setProcessing(true);
        const result = await removeTeamMember(selectedTeam.id, userToRemove);
        if (result.isSuccess) {
          loadData();
          // Refresh selected team
          const updatedTeams = await getTeams(filterDepartment || null);
          if (updatedTeams.isSuccess) {
            const updated = updatedTeams.data.find(t => t.id === selectedTeam.id);
            if (updated) setSelectedTeam(updated);
          }
          setShowRemoveConfirmation(false);
          setUserToRemove(null);
          resolve('Member removed successfully');
        } else {
          reject(new Error(result.errorMessage || 'Failed to remove member'));
        }
      } catch (err) {
        const errorMessage = err.response?.data?.message ||
          err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to remove member';
        reject(new Error(errorMessage));
      } finally {
        setProcessing(false);
      }
    });

    toast.promise(removePromise, {
      loading: 'Removing member...',
      success: (message) => message,
      error: (err) => err.message || 'Failed to remove member',
    });
  };

  const confirmRemoveMember = (userId) => {
    if (!selectedTeam) return;
    
    const member = selectedTeam.members?.find(m => m.userId === userId);
    if (!member) return;
    
    // Check permission before showing confirmation
    if (!canRemoveMember(selectedTeam, member)) {
      toast.error('You do not have permission to remove this member');
      return;
    }
    
    setUserToRemove(userId);
    setShowRemoveConfirmation(true);
  };

  const openEditModal = (team) => {
    // Check permission
    if (!canEditTeam(team)) {
      toast.error('You do not have permission to edit this team');
      return;
    }
    
    setSelectedTeam(team);
    setFormData({
      name: team.name,
      description: team.description || '',
      department: team.department || '',
      leadUserId: team.leadUserId,
      pmUserId: team.pmUserId || ''
    });
    setShowEditModal(true);
  };

  const openMembersModal = (team) => {
    // Check permission
    if (!canManageMembers(team)) {
      toast.error('You do not have permission to manage this team\'s members');
      return;
    }
    
    setSelectedTeam(team);
    setSelectedUsers([]);
    setSearchTerm('');
    setIsDropdownOpen(false);
    setShowMembersModal(true);
  };

  const getAvailableMembersForTeam = () => {
    if (!selectedTeam) return allUsers;
    
    const teamMemberIds = selectedTeam.members?.map(m => m.userId) || [];
    const leadUserId = selectedTeam.leadUserId;
    const allTeamUserIds = [...teamMemberIds, leadUserId];
    
    return allUsers.filter(user => !allTeamUserIds.includes(user.id));
  };

  const getFilteredUsers = () => {
    const availableUsers = getAvailableMembersForTeam();
    
    if (!searchTerm) return availableUsers;
    
    const searchLower = searchTerm.toLowerCase();
    return availableUsers.filter(user => 
      user.firstName?.toLowerCase().includes(searchLower) ||
      user.lastName?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.department?.toLowerCase().includes(searchLower) ||
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchLower)
    );
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const removeSelectedUser = (userId) => {
    setSelectedUsers(prev => prev.filter(id => id !== userId));
  };

  const clearAllSelected = () => {
    setSelectedUsers([]);
  };

  const getSelectedUsersInfo = () => {
    return selectedUsers.map(userId => {
      const user = allUsers.find(u => u.id === userId);
      return user ? {
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
        initials: `${user.firstName?.[0]}${user.lastName?.[0]}` || 'U'
      } : null;
    }).filter(Boolean);
  };

  const getDepartmentColor = (department) => {
    const colors = {
      'engineering': 'bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800',
      'hr': 'bg-green-100 text-green-800 border border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800',
      'finance': 'bg-purple-100 text-purple-800 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800',
      'it': 'bg-indigo-100 text-indigo-800 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800',
      'operations': 'bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800',
      'sales': 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800',
      'marketing': 'bg-pink-100 text-pink-800 border border-pink-200 dark:bg-pink-900/30 dark:text-pink-400 dark:border-pink-800'
    };
    return colors[department?.toLowerCase()] || 'bg-gray-100 text-gray-800 border border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600';
  };

  // Role-based access functions
  const getUserRoleName = (role) => {
    const roleNames = {
      0: 'User',
      1: 'Employee',
      2: 'Lead',
      3: 'PM',
      4: 'Admin'
    };
    return roleNames[role] || 'User';
  };

  const getUserRoleBadge = (role) => {
    const badges = {
      0: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      1: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      2: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      3: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      4: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
    };
    return badges[role] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  if (loading && !teams.length) {
    return <DataTablePageSkeleton />;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <PageFadeSection>
      {/* Header with Current User Info */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div className="relative mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
                <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-7.397a6 6 0 00-9 5.197" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Team Management
                </h1>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                  Create and manage teams, assign leads and members
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-2 py-1 rounded-full ${getUserRoleBadge(currentUserRole)}`}>
                    {getUserRoleName(currentUserRole)}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {currentUserRole === 4 ? 'Full Access' : 
                     currentUserRole === 3 ? 'Manage Your Teams' :
                     currentUserRole === 2 ? 'Team Lead Access' : 'View Only'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-2 left-0 h-0.5 w-24 bg-gradient-to-r from-indigo-500 to-transparent"></div>
        </div>
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Showing {teams.length} teams
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-400 px-4 py-3 rounded mb-4">
          {error}
          <button
            onClick={() => {
              setError(null);
              toast.success('Error cleared');
            }}
            className="float-right hover:text-red-900 dark:hover:text-red-300"
          >
            &times;
          </button>
        </div>
      )}

      </PageFadeSection>
      <PageFadeSection>
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 md:mb-0">Filters</h2>
          {/* My Teams toggle — visible for PM and Lead */}
          {(currentUserRole === 3 || currentUserRole === 2) && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowOnlyMyTeams(false)}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg border transition-colors ${
                  !showOnlyMyTeams
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                All Teams
              </button>
              <button
                onClick={() => setShowOnlyMyTeams(true)}
                className={`px-4 py-2 text-sm font-medium rounded-r-lg border transition-colors ${
                  showOnlyMyTeams
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                My Teams
              </button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Department Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Department</label>
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept.value} value={dept.value}>{dept.name}</option>
                ))}
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex items-end">
              {canCreateTeam() && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="w-full px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-lg hover:from-indigo-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200"
                >
                  <svg className="inline-block w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create New Team
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      </PageFadeSection>
      <PageFadeSection>
      {/* Teams Grid */}
      {(() => {
        const displayTeams = showOnlyMyTeams
          ? teams.filter(t =>
              (currentUserRole === 3 && t.pmUserId === currentUserId) ||
              (currentUserRole === 2 && t.leadUserId === currentUserId))
          : teams;
        return displayTeams.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-7.397a6 6 0 00-9 5.197" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No teams found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating your first team.</p>
          {canCreateTeam() && (
            <div className="mt-6">
              <button
                onClick={() => setShowCreateModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 transition-all duration-200"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create Team
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayTeams.map((team) => (
            <div key={team.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
              <div className="p-6">
                {/* Team Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{team.name}</h3>
                    {team.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{team.description}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDepartmentColor(team.department)}`}>
                      {team.department || 'No Department'}
                    </span>
                  </div>
                </div>

                {/* Team Lead */}
                <div className="flex items-center space-x-3 mb-3">
                  <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold text-sm">
                      {team.leadName?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'TL'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{team.leadName || 'No Lead'}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Team Lead</p>
                  </div>
                </div>

                {/* PM Info */}
                {team.pmName && (
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {team.pmName?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'PM'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{team.pmName}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Project Manager</p>
                    </div>
                  </div>
                )}

                {/* Team Stats */}
                <div className="space-y-3 mb-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Members</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {team.memberCount || 0} member{team.memberCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Created</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {team.createdAt ? parseUTC(team.createdAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2 pt-4 border-t border-gray-200 dark:border-gray-600">
                  {canManageMembers(team) && (
                    <button
                      onClick={() => openMembersModal(team)}
                      className="flex-1 px-3 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/20 rounded-md transition-colors duration-200"
                    >
                      Manage Members
                    </button>
                  )}
                  {!canManageMembers(team) && (
                    <span className="flex-1 px-3 py-2 text-sm font-medium text-gray-400 dark:text-gray-500 cursor-default">
                      View Only
                    </span>
                  )}
                  {canEditTeam(team) && (
                    <button
                      onClick={() => openEditModal(team)}
                      className="px-3 py-2 text-blue-600 hover:text-blue-800 rounded-md hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 transition-colors duration-200"
                      title="Edit Team"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  )}
                  {canDeleteTeam(team) && (
                    <button
                      onClick={() => confirmDeleteTeam(team.id)}
                      className="px-3 py-2 text-red-600 hover:text-red-800 rounded-md hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors duration-200"
                      title="Delete Team"
                    >
                      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      );
      })()}

      </PageFadeSection>

      {/* Create Team Modal - Only shown for Admin/PM */}
      {showCreateModal && canCreateTeam() && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gradient-to-br from-indigo-900/40 via-purple-900/30 to-slate-900/50 backdrop-blur-md transition-opacity"
            onClick={() => {
              setShowCreateModal(false);
              setFormData({ name: '', description: '', department: '', leadUserId: '', pmUserId: '' });
            }}
          />

          {/* Modal Container */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-lg transform transition-all">
              {/* Decorative rings */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-violet-500/20 to-fuchsia-500/20 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 rounded-full blur-xl pointer-events-none" />

              <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-[0_25px_60px_-12px_rgba(79,70,229,0.25)] overflow-hidden border border-white/80 dark:border-gray-700">
                {/* Modal Header — tall gradient with icon */}
                <div className="relative px-7 pt-7 pb-6 bg-gradient-to-br from-indigo-600 via-violet-600 to-purple-700 overflow-hidden">
                  {/* Subtle pattern overlay */}
                  <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />
                  {/* Floating circles */}
                  <div className="absolute top-3 right-10 w-16 h-16 bg-white/10 rounded-full" />
                  <div className="absolute -bottom-4 right-24 w-28 h-28 bg-white/5 rounded-full" />

                  <div className="relative flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 shadow-inner">
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white tracking-tight">Create New Team</h2>
                        <p className="text-indigo-200 text-sm mt-0.5">Add a team to your organization</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowCreateModal(false);
                        setFormData({ name: '', description: '', department: '', leadUserId: '', pmUserId: '' });
                      }}
                      className="text-white/70 hover:text-white p-1.5 hover:bg-white/15 rounded-lg transition-all duration-200 -mt-1 -mr-1"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Modal Body */}
                <div className="px-7 py-6 space-y-5">
                  {/* Team Name */}
                  <div className="group">
                    <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                      Team Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-gray-50/80 dark:bg-gray-700/80 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)] transition-all duration-200 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm font-medium"
                      placeholder="e.g. Frontend Engineering"
                    />
                  </div>

                  {/* Description */}
                  <div className="group">
                    <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={2}
                      className="w-full px-4 py-3 bg-gray-50/80 dark:bg-gray-700/80 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)] transition-all duration-200 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm font-medium resize-none"
                      placeholder="Brief team description..."
                    />
                  </div>

                  {/* Two-column row: Department + Lead */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="group">
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        Department
                      </label>
                      <div className="relative">
                        <select
                          value={formData.department}
                          onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-50/80 dark:bg-gray-700/80 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)] transition-all duration-200 text-gray-800 dark:text-gray-100 text-sm font-medium appearance-none cursor-pointer"
                        >
                          <option value="">Select...</option>
                          {departments.map((dept) => (
                            <option key={dept.value} value={dept.value}>{dept.name}</option>
                          ))}
                        </select>
                        <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                      </div>
                    </div>

                    <div className="group">
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                        Team Lead <span className="text-red-400">*</span>
                      </label>
                      <div className="relative">
                        <select
                          value={formData.leadUserId}
                          onChange={(e) => {
                            setFormData({ ...formData, leadUserId: e.target.value });
                            setLeadWarningData(null);
                          }}
                          className="w-full px-4 py-3 bg-gray-50/80 dark:bg-gray-700/80 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)] transition-all duration-200 text-gray-800 dark:text-gray-100 text-sm font-medium appearance-none cursor-pointer"
                        >
                          <option value="">Select...</option>
                          {leads.map((lead) => (
                            <option key={lead.id} value={lead.id}>{lead.fullName}</option>
                          ))}
                        </select>
                        <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                      </div>
                    </div>
                  </div>

                  {/* PM Dropdown - Only visible to Admin */}
                  {currentUserRole === 4 && (
                    <div className="group">
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                        Project Manager
                      </label>
                      <div className="relative">
                        <select
                          value={formData.pmUserId}
                          onChange={(e) => setFormData({ ...formData, pmUserId: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-50/80 dark:bg-gray-700/80 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl focus:outline-none focus:border-indigo-500 focus:bg-white dark:focus:bg-gray-700 focus:shadow-[0_0_0_4px_rgba(99,102,241,0.1)] transition-all duration-200 text-gray-800 dark:text-gray-100 text-sm font-medium appearance-none cursor-pointer"
                        >
                          <option value="">Select PM...</option>
                          {pms.map((pm) => (
                            <option key={pm.id} value={pm.id}>{pm.fullName}</option>
                          ))}
                        </select>
                        <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                      </div>
                    </div>
                  )}
                </div>

                {/* Modal Footer */}
                <div className="px-7 py-5 bg-gradient-to-r from-gray-50/80 to-slate-50/80 dark:from-gray-800 dark:to-gray-800 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setFormData({ name: '', description: '', department: '', leadUserId: '', pmUserId: '' });
                    }}
                    disabled={processing}
                    className="px-5 py-2.5 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-300 active:scale-[0.98] transition-all duration-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateTeam}
                    disabled={processing}
                    className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl hover:from-indigo-700 hover:to-violet-700 shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40 active:scale-[0.98] focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                  >
                    {processing ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
                        Create Team
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditModal && selectedTeam && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gradient-to-br from-blue-900/40 via-slate-900/30 to-indigo-900/50 backdrop-blur-md transition-opacity"
            onClick={() => {
              setShowEditModal(false);
              setSelectedTeam(null);
            }}
          />

          {/* Modal Container */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-lg transform transition-all">
              {/* Decorative rings */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-indigo-500/20 to-violet-500/20 rounded-full blur-xl pointer-events-none" />

              <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-[0_25px_60px_-12px_rgba(37,99,235,0.25)] overflow-hidden border border-white/80 dark:border-gray-700">
                {/* Modal Header */}
                <div className="relative px-7 pt-7 pb-6 bg-gradient-to-br from-blue-600 via-blue-600 to-indigo-700 overflow-hidden">
                  <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />
                  <div className="absolute top-3 right-10 w-16 h-16 bg-white/10 rounded-full" />
                  <div className="absolute -bottom-4 right-24 w-28 h-28 bg-white/5 rounded-full" />

                  <div className="relative flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 shadow-inner">
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white tracking-tight">Edit Team</h2>
                        <p className="text-blue-200 text-sm mt-0.5">{selectedTeam.name}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setShowEditModal(false);
                        setSelectedTeam(null);
                      }}
                      className="text-white/70 hover:text-white p-1.5 hover:bg-white/15 rounded-lg transition-all duration-200 -mt-1 -mr-1"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

              {/* Modal Body */}
              <div className="px-7 py-6 space-y-5">
                <div className="group">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                    Team Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-gray-50/80 dark:bg-gray-700/80 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-700 focus:shadow-[0_0_0_4px_rgba(59,130,246,0.1)] transition-all duration-200 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm font-medium"
                    placeholder="Enter team name"
                  />
                </div>

                <div className="group">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-3 bg-gray-50/80 dark:bg-gray-700/80 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-700 focus:shadow-[0_0_0_4px_rgba(59,130,246,0.1)] transition-all duration-200 text-gray-800 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 text-sm font-medium resize-none"
                    placeholder="Brief team description..."
                  />
                </div>

                <div className="group">
                  <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    Team Lead <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <select
                      value={formData.leadUserId}
                      onChange={(e) => {
                        setFormData({ ...formData, leadUserId: e.target.value });
                        setLeadWarningData(null);
                      }}
                      className="w-full px-4 py-3 bg-gray-50/80 dark:bg-gray-700/80 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-700 focus:shadow-[0_0_0_4px_rgba(59,130,246,0.1)] transition-all duration-200 text-gray-800 dark:text-gray-100 text-sm font-medium appearance-none cursor-pointer"
                    >
                      <option value="">Select Team Lead</option>
                      {leads.map((lead) => (
                        <option key={lead.id} value={lead.id}>{lead.fullName}</option>
                      ))}
                    </select>
                    <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                  </div>
                </div>

                {/* PM Dropdown - Only visible to Admin */}
                {currentUserRole === 4 && (
                  <div className="group">
                    <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />
                      Project Manager
                    </label>
                    <div className="relative">
                      <select
                        value={formData.pmUserId}
                        onChange={(e) => setFormData({ ...formData, pmUserId: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-50/80 dark:bg-gray-700/80 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-gray-700 focus:shadow-[0_0_0_4px_rgba(59,130,246,0.1)] transition-all duration-200 text-gray-800 dark:text-gray-100 text-sm font-medium appearance-none cursor-pointer"
                      >
                        <option value="">Select PM...</option>
                        {pms.map((pm) => (
                          <option key={pm.id} value={pm.id}>{pm.fullName}</option>
                        ))}
                      </select>
                      <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="px-7 py-5 bg-gradient-to-r from-gray-50/80 to-slate-50/80 dark:from-gray-800 dark:to-gray-800 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedTeam(null);
                  }}
                  disabled={processing}
                  className="px-5 py-2.5 text-sm font-semibold text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 hover:border-gray-300 active:scale-[0.98] transition-all duration-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateTeam}
                  disabled={processing}
                  className="px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 active:scale-[0.98] focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                >
                  {processing ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      Save Changes
                    </span>
                  )}
                </button>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Members Modal */}
      {showMembersModal && selectedTeam && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-gradient-to-br from-emerald-900/40 via-slate-900/30 to-teal-900/50 backdrop-blur-md transition-opacity"
            onClick={() => {
              setShowMembersModal(false);
              setSelectedTeam(null);
              setSelectedUsers([]);
              setSearchTerm('');
            }}
          />

          {/* Modal Container */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-4xl transform transition-all">
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-green-500/20 to-cyan-500/20 rounded-full blur-xl pointer-events-none" />

              <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-[0_25px_60px_-12px_rgba(16,185,129,0.25)] overflow-hidden border border-white/80 dark:border-gray-700">
              {/* Modal Header */}
              <div className="relative px-7 pt-7 pb-6 bg-gradient-to-br from-emerald-600 via-green-600 to-teal-700 overflow-hidden">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />
                <div className="absolute top-3 right-10 w-16 h-16 bg-white/10 rounded-full" />
                <div className="absolute -bottom-4 right-24 w-28 h-28 bg-white/5 rounded-full" />

                <div className="relative flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 shadow-inner">
                      <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                      </svg>
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-white tracking-tight">Manage Members</h2>
                      <p className="text-emerald-200 text-sm mt-0.5">{selectedTeam.name}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowMembersModal(false);
                      setSelectedTeam(null);
                      setSelectedUsers([]);
                      setSearchTerm('');
                    }}
                    className="text-white/70 hover:text-white p-1.5 hover:bg-white/15 rounded-lg transition-all duration-200 -mt-1 -mr-1"
                  >
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="px-7 py-6 space-y-8">
                  {/* Current Members Section */}
                  <div className="space-y-5">
                    <div className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                        Current Members ({selectedTeam.members?.length || 0})
                      </h3>
                    </div>

                    {selectedTeam.members?.length === 0 ? (
                      <div className="bg-emerald-50/50 dark:bg-emerald-900/20 border-2 border-dashed border-emerald-200/60 dark:border-emerald-800/60 rounded-xl p-8 text-center">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <h4 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No members in this team</h4>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Add members using the form below.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {selectedTeam.members?.map((member) => (
                          <div key={member.id} className="bg-gradient-to-r from-gray-50/80 to-emerald-50/30 dark:from-gray-700/50 dark:to-emerald-900/10 rounded-xl p-4 border border-gray-100 dark:border-gray-600 hover:border-emerald-200/60 dark:hover:border-emerald-700/60 transition-all duration-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <div className="h-10 w-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center shadow-sm">
                                  <span className="text-white font-semibold text-sm">
                                    {member.userName?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'ME'}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2">
                                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                      {member.userName}
                                    </p>
                                    {member.userId === selectedTeam.leadUserId && (
                                      <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full">
                                        Team Lead
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]">{member.email}</p>
                                </div>
                              </div>
                              {member.userId !== selectedTeam.leadUserId && (
                                <button
                                  onClick={() => confirmRemoveMember(member.userId)}
                                  disabled={processing}
                                  className="text-rose-500 hover:text-rose-700 hover:bg-rose-50/80 px-3 py-1.5 rounded-xl text-sm font-medium border border-transparent hover:border-rose-200/60 transition-all duration-200 disabled:opacity-50"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add Members Section */}
                  <div className="space-y-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                          Add Members
                        </h3>
                      </div>
                      {selectedUsers.length > 0 && (
                        <button
                          onClick={clearAllSelected}
                          className="text-sm font-medium text-rose-600 hover:text-rose-800 transition-colors"
                        >
                          Clear All
                        </button>
                      )}
                    </div>

                    {/* Selected Users Preview */}
                    {selectedUsers.length > 0 && (
                      <div className="bg-blue-50/70 rounded-xl p-4 border-2 border-blue-200/60">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-blue-800 dark:text-blue-400">
                            Selected ({selectedUsers.length})
                          </span>
                          <button
                            onClick={handleAddSelectedMembers}
                            disabled={processing}
                            className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-700 focus:shadow-[0_0_0_4px_rgba(16,185,129,0.15)] transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
                          >
                            {processing ? 'Adding...' : `Add ${selectedUsers.length} Member(s)`}
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {getSelectedUsersInfo().map(user => (
                            <div
                              key={user.id}
                              className="flex items-center gap-2 bg-white dark:bg-gray-700 px-3 py-2 rounded-xl border border-blue-200/60 dark:border-blue-800/60 shadow-sm"
                            >
                              <div className="h-6 w-6 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg flex items-center justify-center">
                                <span className="text-xs font-medium text-blue-800 dark:text-blue-400">{user.initials}</span>
                              </div>
                              <div>
                                <p className="text-sm text-gray-800 dark:text-gray-200">{user.name}</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[150px]">{user.email}</p>
                              </div>
                              <button
                                onClick={() => removeSelectedUser(user.id)}
                              className="ml-2 text-gray-400 hover:text-rose-600 transition-colors"
                              >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Multi-select Dropdown */}
                    <div className="relative">
                      <div className="flex items-center space-x-3">
                        <div className="flex-1">
                          <div className="relative">
                            <input
                              type="text"
                              value={searchTerm}
                              onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setIsDropdownOpen(true);
                              }}
                              onFocus={() => setIsDropdownOpen(true)}
                              placeholder="Search users by name, email, or department..."
                              className="w-full px-4 py-3 pl-10 border-2 border-gray-200/80 dark:border-gray-600 dark:bg-gray-700 rounded-xl focus:outline-none focus:border-emerald-400 focus:shadow-[0_0_0_4px_rgba(16,185,129,0.1)] transition-all duration-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 dark:text-gray-100"
                            />
                            <svg
                              className="absolute left-3 top-3.5 h-5 w-5 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                              />
                            </svg>
                            {searchTerm && (
                              <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-3.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                              >
                                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {getAvailableMembersForTeam().length} available
                        </div>
                      </div>

                      {/* Dropdown List */}
                      {isDropdownOpen && getFilteredUsers().length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] max-h-96 overflow-y-auto">
                          <div className="p-2">
                            {getFilteredUsers().map(user => (
                              <div
                                key={user.id}
                                className={`flex items-center justify-between p-3 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20 rounded-xl cursor-pointer transition-all duration-200 ${
                                  selectedUsers.includes(user.id) ? 'bg-emerald-50/70 dark:bg-emerald-900/30 border border-emerald-200/60 dark:border-emerald-700/60' : ''
                                }`}
                                onClick={() => toggleUserSelection(user.id)}
                              >
                                <div className="flex items-center space-x-3">
                                  <div className="flex items-center">
                                    <div className={`h-6 w-6 rounded-lg flex items-center justify-center mr-3 transition-all duration-200 ${
                                      selectedUsers.includes(user.id) 
                                        ? 'bg-emerald-100 dark:bg-emerald-900/40 border-2 border-emerald-500' 
                                        : 'bg-gray-100 dark:bg-gray-600 border-2 border-gray-300 dark:border-gray-500'
                                    }`}>
                                      {selectedUsers.includes(user.id) && (
                                        <svg className="h-3 w-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </div>
                                    <div className="h-10 w-10 bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-600 dark:to-gray-700 rounded-xl flex items-center justify-center border border-gray-200/60 dark:border-gray-500">
                                      <span className="text-gray-600 dark:text-gray-300 font-semibold text-sm">
                                        {user.firstName?.[0]}{user.lastName?.[0] || 'U'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center space-x-2">
                                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                        {user.firstName} {user.lastName}
                                      </p>
                                      {user.role === 2 && (
                                        <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full">
                                          Lead
                                        </span>
                                      )}
                                      {user.role === 4 && (
                                        <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 rounded-full">
                                          Admin
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                                      <p className="truncate">{user.email}</p>
                                      <span>•</span>
                                      <p className="truncate">{user.department || 'No Department'}</p>
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleUserSelection(user.id);
                                  }}
                                  className={`ml-2 px-3 py-1.5 text-sm font-semibold rounded-xl transition-all duration-200 ${
                                    selectedUsers.includes(user.id)
                                      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-200 dark:hover:bg-emerald-900/50'
                                      : 'bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                                  }`}
                                >
                                  {selectedUsers.includes(user.id) ? 'Selected' : 'Select'}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* No results message */}
                      {isDropdownOpen && searchTerm && getFilteredUsers().length === 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl shadow-lg p-4 text-center">
                          <p className="text-sm text-gray-600 dark:text-gray-400">No users found matching "{searchTerm}"</p>
                        </div>
                      )}

                      {/* All users already in team message */}
                      {getAvailableMembersForTeam().length === 0 && (
                        <div className="mt-4 bg-emerald-50/50 dark:bg-emerald-900/20 border border-emerald-200/40 dark:border-emerald-800/40 rounded-xl p-4 text-center">
                          <p className="text-sm text-gray-600 dark:text-gray-400">All users are already in this team</p>
                        </div>
                      )}
                    </div>

                    {/* Quick Add Selected Button (for small screens) */}
                    {selectedUsers.length > 0 && (
                      <div className="lg:hidden">
                        <button
                          onClick={handleAddSelectedMembers}
                          disabled={processing}
                          className="w-full px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-semibold rounded-xl hover:from-emerald-600 hover:to-teal-700 focus:shadow-[0_0_0_4px_rgba(16,185,129,0.15)] transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
                        >
                          {processing ? 'Adding...' : `Add ${selectedUsers.length} Selected Member(s)`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-7 py-4 border-t border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50/80 to-emerald-50/40 dark:from-gray-800 dark:to-gray-800 flex justify-between items-center">
                <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                  {selectedUsers.length > 0 && (
                    <span>{selectedUsers.length} user(s) selected</span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowMembersModal(false);
                      setSelectedTeam(null);
                      setSelectedUsers([]);
                      setSearchTerm('');
                    }}
                    className="px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 active:scale-[0.98]"
                  >
                    Close
                  </button>
                  {selectedUsers.length > 0 && (
                    <button
                      onClick={handleAddSelectedMembers}
                      disabled={processing}
                      className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl hover:from-emerald-700 hover:to-teal-700 focus:shadow-[0_0_0_4px_rgba(16,185,129,0.15)] transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
                    >
                      {processing ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Adding...
                        </span>
                      ) : (
                        `Add ${selectedUsers.length} Member(s)`
                      )}
                    </button>
                  )}
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Confirmation Modal */}
      {showRemoveConfirmation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-gradient-to-br from-rose-900/40 via-slate-900/30 to-red-900/50 backdrop-blur-md transition-opacity"
            onClick={() => { setShowRemoveConfirmation(false); setUserToRemove(null); }}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md transform transition-all">
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-rose-500/20 to-red-500/20 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-pink-500/20 to-rose-500/20 rounded-full blur-xl pointer-events-none" />

              <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-[0_25px_60px_-12px_rgba(225,29,72,0.25)] overflow-hidden border border-white/80 dark:border-gray-700">
                {/* Header */}
                <div className="relative px-7 pt-7 pb-6 bg-gradient-to-br from-rose-600 via-red-600 to-rose-700 overflow-hidden">
                  <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />
                  <div className="absolute top-3 right-10 w-16 h-16 bg-white/10 rounded-full" />
                  <div className="absolute -bottom-4 right-24 w-28 h-28 bg-white/5 rounded-full" />
                  <div className="relative flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 shadow-inner">
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M22 10.5h-6m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white tracking-tight">Remove Member</h2>
                        <p className="text-rose-200 text-sm mt-0.5">Confirm member removal</p>
                      </div>
                    </div>
                    <button
                      onClick={() => { setShowRemoveConfirmation(false); setUserToRemove(null); }}
                      className="text-white/70 hover:text-white p-1.5 hover:bg-white/15 rounded-lg transition-all duration-200 -mt-1 -mr-1"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="px-7 py-8">
                  <div className="flex items-center justify-center mb-6">
                    <div className="h-20 w-20 bg-gradient-to-br from-rose-100 to-red-100 rounded-2xl flex items-center justify-center border border-rose-200/60">
                      <svg className="h-10 w-10 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.928-.833-2.698 0L4.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                    Are you sure?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-center leading-relaxed">
                    This member will be removed from the team. They can be added back later if needed.
                  </p>
                </div>

                {/* Footer */}
                <div className="px-7 py-4 border-t border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50/80 to-rose-50/40 dark:from-gray-800 dark:to-gray-800 flex justify-end gap-3">
                  <button
                    onClick={() => { setShowRemoveConfirmation(false); setUserToRemove(null); }}
                    disabled={processing}
                    className="px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 active:scale-[0.98] disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleRemoveMember}
                    disabled={processing}
                    className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-rose-600 to-red-600 rounded-xl hover:from-rose-700 hover:to-red-700 focus:shadow-[0_0_0_4px_rgba(225,29,72,0.15)] transition-all duration-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {processing ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Removing...
                      </span>
                    ) : (
                      'Yes, Remove Member'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Team Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-gradient-to-br from-red-900/40 via-slate-900/30 to-rose-900/50 backdrop-blur-md transition-opacity"
            onClick={() => { setShowDeleteConfirmation(false); setTeamToDelete(null); }}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-md transform transition-all">
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-gradient-to-br from-red-500/20 to-rose-500/20 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-gradient-to-br from-rose-500/20 to-pink-500/20 rounded-full blur-xl pointer-events-none" />

              <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-[0_25px_60px_-12px_rgba(220,38,38,0.25)] overflow-hidden border border-white/80 dark:border-gray-700">
                {/* Header */}
                <div className="relative px-7 pt-7 pb-6 bg-gradient-to-br from-red-600 via-red-600 to-rose-700 overflow-hidden">
                  <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />
                  <div className="absolute top-3 right-10 w-16 h-16 bg-white/10 rounded-full" />
                  <div className="absolute -bottom-4 right-24 w-28 h-28 bg-white/5 rounded-full" />
                  <div className="relative flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 shadow-inner">
                        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                        </svg>
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-white tracking-tight">Delete Team</h2>
                        <p className="text-red-200 text-sm mt-0.5">This action cannot be undone</p>
                      </div>
                    </div>
                    <button
                      onClick={() => { setShowDeleteConfirmation(false); setTeamToDelete(null); }}
                      className="text-white/70 hover:text-white p-1.5 hover:bg-white/15 rounded-lg transition-all duration-200 -mt-1 -mr-1"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Body */}
                <div className="px-7 py-8">
                  <div className="flex items-center justify-center mb-6">
                    <div className="h-20 w-20 bg-gradient-to-br from-red-100 to-rose-100 rounded-2xl flex items-center justify-center border border-red-200/60">
                      <svg className="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                    Delete this team?
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-center mb-6 leading-relaxed">
                    All team members will be removed from this team. This action cannot be undone and you will lose all team data.
                  </p>
                  <div className="bg-red-50/70 dark:bg-red-900/20 border-2 border-red-200/60 dark:border-red-800/60 rounded-xl p-4">
                    <div className="flex items-start">
                      <svg className="h-5 w-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.928-.833-2.698 0L4.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <p className="text-sm text-red-800 dark:text-red-400 leading-relaxed">
                        Warning: This will permanently delete the team and remove all members from it.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="px-7 py-4 border-t border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50/80 to-red-50/40 dark:from-gray-800 dark:to-gray-800 flex justify-end gap-3">
                  <button
                    onClick={() => { setShowDeleteConfirmation(false); setTeamToDelete(null); }}
                    className="px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 active:scale-[0.98]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteTeam}
                    className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-red-600 to-rose-600 rounded-xl hover:from-red-700 hover:to-rose-700 focus:shadow-[0_0_0_4px_rgba(220,38,38,0.15)] transition-all duration-200 active:scale-[0.98]"
                  >
                    Yes, Delete Team
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lead Warning Modal - Multi-team lead confirmation */}
      {showLeadWarning && leadWarningData && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div
            className="fixed inset-0 bg-gradient-to-br from-amber-900/40 via-slate-900/30 to-orange-900/50 backdrop-blur-md transition-opacity"
            onClick={() => { setShowLeadWarning(false); setLeadWarningData(null); }}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-sm transform transition-all">
              <div className="absolute -top-5 -right-5 w-28 h-28 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-3 -left-3 w-20 h-20 bg-gradient-to-br from-yellow-500/20 to-amber-500/20 rounded-full blur-xl pointer-events-none" />

              <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-[0_25px_60px_-12px_rgba(245,158,11,0.25)] overflow-hidden border border-white/80 dark:border-gray-700">
                {/* Header */}
                <div className="relative px-7 pt-6 pb-5 bg-gradient-to-br from-amber-500 via-amber-500 to-orange-600 overflow-hidden">
                  <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '20px 20px' }} />
                  <div className="absolute top-2 right-8 w-14 h-14 bg-white/10 rounded-full" />
                  <div className="absolute -bottom-3 right-20 w-24 h-24 bg-white/5 rounded-full" />
                  <div className="relative flex items-center gap-4">
                    <div className="w-11 h-11 bg-white/15 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/20 shadow-inner">
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-bold text-white tracking-tight">Multiple Teams Warning</h3>
                  </div>
                </div>

                {/* Body */}
                <div className="px-7 py-6">
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    <span className="font-semibold text-gray-900 dark:text-white">{leadWarningData.leadName}</span> is already assigned as lead to{' '}
                    <span className="font-semibold text-amber-600">{leadWarningData.teamCount} other team{leadWarningData.teamCount > 1 ? 's' : ''}</span>.
                    Do you want them to have multiple teams?
                  </p>
                </div>

                {/* Footer */}
                <div className="px-7 py-4 border-t border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50/80 to-amber-50/40 dark:from-gray-800 dark:to-gray-800 flex justify-end gap-3">
                  <button
                    onClick={() => { setShowLeadWarning(false); setLeadWarningData(null); }}
                    className="px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border-2 border-gray-200/80 dark:border-gray-600 rounded-xl hover:border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-all duration-200 active:scale-[0.98]"
                  >
                    No
                  </button>
                  <button
                    onClick={() => {
                      setShowLeadWarning(false);
                      setLeadWarningData({ ...leadWarningData, confirmed: true });
                      if (leadWarningData.action === 'create') {
                        setTimeout(() => handleCreateTeam(), 100);
                      } else {
                        setTimeout(() => handleUpdateTeam(), 100);
                      }
                    }}
                    className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl hover:from-amber-600 hover:to-orange-600 focus:shadow-[0_0_0_4px_rgba(245,158,11,0.15)] transition-all duration-200 active:scale-[0.98]"
                  >
                    Yes, Assign
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManagementPage;